import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { RANK_TRACKER_DEMO_CAMPAIGNS_CONFIG_KEY } from "app/common/campaigns/KeywordsCampaignConsts";
import { enrichCampaignsWithIcons } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import { fetchDemoCampaignList } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type { IKeywordsCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { createRankTrackerQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";

import { useRankTrackerQuery } from "../useRankTrackerQuery";

export const fetchDemoCampaigns = async (): Promise<IKeywordsCampaignData[]> => {
    try {
        const managedCampaigns = (await fetchDemoCampaignList()) as IKeywordsCampaignData[];
        const enrichedCampaigns = await enrichCampaignsWithIcons(managedCampaigns);
        localStorage.setItem(RANK_TRACKER_DEMO_CAMPAIGNS_CONFIG_KEY, JSON.stringify(enrichedCampaigns));
        return enrichedCampaigns;
    } catch (error) {
        Sentry.errorTracker({
            e: error,
            message: "Error fetching demo campaigns",
            level: "error",
        });
        return [];
    }
};

type QueryOptions = Omit<UseQueryOptions<IKeywordsCampaignData[]>, "queryKey" | "queryFn">;

const DEMO_CAMPAIGNS_QUERY_KEY: QueryKey = createRankTrackerQueryKey(RankTrackerQueryType.DemoCampaigns);

export const useDemoCampaignsQuery = (options?: QueryOptions) => {
    return useRankTrackerQuery({
        queryKey: DEMO_CAMPAIGNS_QUERY_KEY,
        queryFn: () => fetchDemoCampaigns(),
        throwOnError: (error) => {
            Sentry.errorTracker({
                e: error,
                level: "error",
                message: "Error fetching demo campaigns configurations",
            });
            return false;
        },
        ...options,
    });
};
